import React, { useRef, useState, useCallback } from 'react';
import { Modal, Button, Space, Spin, Flex } from 'antd';
import Webcam from 'react-webcam';
import type { RcFile } from 'antd/es/upload';
import { useTranslation } from 'react-i18next';

export interface TakePhotoProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: RcFile) => void;
}

const TakePhoto: React.FC<TakePhotoProps> = ({ open, onClose, onCapture }) => {
  const { t } = useTranslation();

  const webcamRef = useRef<Webcam>(null);
  const [capturing, setCapturing] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 },
  };

  const handleCapture = useCallback(() => {
    setCapturing(true);
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      // Convert base64 to blob
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          // Create a File object from the blob
          const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' }) as RcFile;
          onCapture(file);
          setCapturing(false);
          onClose();
        })
        .catch(err => {
          console.error('Error capturing photo:', err);
          setCapturing(false);
        });
    } else {
      setCapturing(false);
    }
  }, [onCapture, onClose]);

  const handleSwitchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  return (
    <Modal
      title={t('components.takePhoto')}
      open={open}
      onCancel={onClose}
      footer={
        <Flex justify='end' gap={8}>
          <Button onClick={handleSwitchCamera}>
            {t('components.switchCamera')}
          </Button>
          <Button type="primary" onClick={handleCapture} loading={capturing}>
            {t('components.takePhoto')}
          </Button>
          <Button onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </Flex>
      }
      width={640}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', backgroundColor: '#000' }}>
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      </Space>
    </Modal>
  );
};

export default TakePhoto;
